import { useEffect, useState } from 'react';
import ControlHeader from '../components/receiving/ControlHeader';
import ReceivingPanel from '../components/receiving/ReceivingPanel';
import ActionPanel from '../components/receiving/ActionPanel';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAuth } from '../../wms/context/wms-context';
import Notification from '../../components/Notification';
import ReceiveNewLineModal from '../components/receiving/ReceiveNewLineModal';
import { getTableStructures } from '../../utils/commonAPICalls';
import { useSearchParams } from 'react-router-dom';
import BinderCheckPanel from '../components/receiving/BinderCheckPanel';
import config from '../../utils/config';
import logError from '../../utils/errorUtil';

export default function WMSReceiving() {
    const { currentUser, setCurrentUser } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [status, setStatus] = useState({
        loading: false,
        error: false,
        errorMessage: '',
        receivingPanel: false,
        binderCheckPanel: false,
        actionPanel: false,
        receiveNewLinePanel: false,
        partNumber: '',
    });

    const ORIGIN = config.origin;

    const [activeDocument, setActiveDocument] = useState(null);
    const [loadingDocument, setLoadingDocument] = useState(false);

    const [selectedMaid, setSelectedMaid] = useState('');

    const [inventoryLineId, setInventoryLineId] = useState(currentUser?.receiving_inventoryId);
    const partNumber =
        currentUser?.selectedMaidLine?.inscor__Part_Master__rName || currentUser?.receivingFormData?.PartNumber;
    const ownerCode = currentUser?.selectedMaidLine?.inscor__Owner_Code__rName || currentUser?.receivingFormData?.OwnerCode;

    const [showSavedNotification, setShowSavedNotification] = useState({
        show: false,
        title: '',
        desc: '',
        status: '',
    });

    const closeActionPanel = () => {
        if (searchParams.has('editinventory')) {
            searchParams.delete('editinventory');
            searchParams.delete('inventoryId');
            setSearchParams(searchParams);
        }
        setStatus({ ...status, actionPanel: false });
        setCurrentUser({ ...currentUser, receiving_inventoryId: null, receiving_inventoryLine: null });
        currentUser.receiving_inventoryId = null;
        currentUser.receiving_inventoryLine = null;
    };

    useEffect(() => {
        getTableStructures(currentUser, setCurrentUser);
    }, []);

    // Check for search params
    useEffect(() => {
        if (searchParams.has('editinventory')) {
            setStatus((prevStatus) => ({
                ...prevStatus,
                actionPanel: true,
            }));
        }
    }, [searchParams]);

    const defaultDocumentData = {
        documents: [],
        loading: true,
        error: false,
    };

    const [pacAirData, setPacAirData] = useState({
        ...defaultDocumentData,
    });

    useEffect(() => {
        if (!inventoryLineId) {
            if (searchParams.has('inventoryId') && searchParams.has('editinventory')) {
                const inventoryIdParam = searchParams.get('inventoryId');
                setInventoryLineId(inventoryIdParam);
            }
            if (currentUser?.receiving_inventoryId) {
                setInventoryLineId(currentUser?.receiving_inventoryId);
            }
        }
    }, [inventoryLineId, currentUser]);

    async function fetchPageData() {
        setLoadingDocument(true);

        let error = false;
        let updatedDocumentData;

        const dataToSend = {
            authentication: {
                userId: currentUser.authentication.userId,
                userEmail: currentUser.authentication.userEmail,
                userCompany: currentUser.authentication.userCompany || localStorage.getItem('company'),
            },
            partNumber,
            ownerCode,
            manifestName:
                currentUser?.selectedMaid?.Name === 'Multiple Manifests'
                    ? currentUser?.selectedMaidLine?.manifestName
                    : currentUser?.selectedMaid?.Name,
        };

        try {
            const response = await fetch(`${ORIGIN}/document/receiving`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(dataToSend),
            });

            const data = await response.json();

            if (!response || !response.ok) {
                setPacAirData({ ...pacAirData, documents: [] });
                return;
            }

            if (data.status === 204) {
                setActiveDocument({ empty: true });
                return;
            }

            const updatedDocuments = data?.documentList?.map((doc) =>
                doc.documentId === data?.firstDocumentData?.documentId ? { ...doc, ...data.firstDocumentData } : doc
            );

            setActiveDocument(data.firstDocumentData ?? null);
            updatedDocumentData = updatedDocuments ?? [];
        } catch (error) {
            // Set loading state to false
            setPacAirData({
                ...pacAirData,
                documents: [],
                error: true,
                loading: false,
            });
            setLoadingDocument(false);
            logError(error, 'Receiving fetchPageData');
            setShowSavedNotification({
                show: true,
                title: 'Error',
                desc: 'There was an error fetching the documents. Please refresh to try again.',
                status: 'error',
            });
        } finally {
            setPacAirData({
                ...pacAirData,
                documents: updatedDocumentData,
                error: error,
                loading: false,
            });
            setLoadingDocument(false);
        }
    }

    useEffect(() => {
        if (status?.receivingPanel) {
            setTimeout(() => {
                fetchPageData();
            }, 2000);
        }
    }, [status.receivingPanel]);

    return (
        <div>
            {/* Control header */}
            <ControlHeader
                status={status}
                setStatus={setStatus}
                selectedMaid={selectedMaid}
                setSelectedMaid={setSelectedMaid}
            />
            {/* Loading spinner */}
            {status.loading && <LoadingSpinner />}
            {/* Modal */}
            {status.receivingPanel && <ReceivingPanel status={status} setStatus={setStatus} />}
            {/* Binder check panel */}
            {status.binderCheckPanel && (
                <BinderCheckPanel
                    activeDocument={activeDocument}
                    inventoryLineId={inventoryLineId}
                    loadingDocument={loadingDocument}
                    pacAirData={pacAirData}
                    setShowSavedNotification={setShowSavedNotification}
                    setStatus={setStatus}
                    showSavedNotification={showSavedNotification}
                    status={status}
                />
            )}
            {/* Action panel */}
            {status.actionPanel && (
                <ActionPanel
                    status={status.actionPanel}
                    setStatus={setStatus}
                    closeActionPanel={closeActionPanel}
                    canClose={searchParams.get('editinventory') ? true : false}
                    isReceiving={true}
                    selectedMaid={selectedMaid}
                />
            )}

            {status.receiveNewLinePanel && (
                <ReceiveNewLineModal
                    status={status}
                    setStatus={setStatus}
                    selectedMaid={selectedMaid}
                    setSelectedMaid={setSelectedMaid}
                />
            )}
            {/* Error */}
            {status.error && (
                <Notification
                    showSavedNotification={status.error}
                    setShowSavedNotification={setStatus}
                    title='Internal Error'
                    desc={
                        status.errorMessage ||
                        'There was an error. Please refresh and try again. If the problem persists, please contact your administrator.'
                    }
                    status='error'
                />
            )}
        </div>
    );
}
